import { useMemo } from 'react';
import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { Badge, Button, Card, PageHeader, Spinner, cx } from '../../ui/index.tsx';
import { useMoney } from '../products/data.ts';

const dateFormat = new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' });

/** Turnos de todas las cajas: abiertos y cerrados, con su diferencia (solo el dueño). */
export function CashHistoryPage() {
  const { business } = useSession();
  const money = useMoney();
  const data = useLiveQuery(async () => {
    const [sessions, devices, members, registers] = await Promise.all([
      db.cash_sessions.where('business_id').equals(business.id).toArray(),
      db.devices.where('business_id').equals(business.id).toArray(),
      db.memberships.where('business_id').equals(business.id).toArray(),
      db.registers.where('business_id').equals(business.id).toArray(),
    ]);
    return { sessions, devices, members, registers };
  }, [business.id]);

  const view = useMemo(() => {
    if (!data) return null;
    const device = new Map(data.devices.map((d) => [d.id, d.name]));
    const register = new Map(data.registers.map((r) => [r.id, r.name]));
    const mainRegister = data.registers.find((r) => r.is_default)?.name ?? 'Caja';
    const person = new Map(data.members.map((m) => [m.user_id, m.display_name]));
    const sessions = [...data.sessions].sort((a, b) => b.opened_at.localeCompare(a.opened_at));
    const closed = sessions.filter((s) => s.closed_at && s.difference != null);
    return {
      sessions,
      device: (id: string | null) => (id ? (device.get(id) ?? 'Dispositivo') : '—'),
      register: (id: string | null | undefined) => (id ? (register.get(id) ?? mainRegister) : mainRegister),
      person: (id: string | null | undefined) => (id ? (person.get(id) ?? '—') : '—'),
      totalDifference: closed.reduce((a, s) => a + (s.difference ?? 0), 0),
      withDifference: closed.filter((s) => s.difference !== 0).length,
    };
  }, [data]);

  if (!view) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <PageHeader
        title="Historial de cajas"
        description="Aperturas y cierres de todas tus cajas. El efectivo esperado lo calcula el servidor con las ventas y movimientos de cada caja."
        actions={
          <Link to="/caja">
            <Button variant="secondary">Volver</Button>
          </Link>
        }
      />
      <p className="text-sm text-slate-600">
        {view.withDifference} {view.withDifference === 1 ? 'cierre' : 'cierres'} con diferencia · suma de diferencias{' '}
        <strong className={cx(view.totalDifference < 0 ? 'text-red-700' : view.totalDifference > 0 ? 'text-amber-700' : 'text-emerald-700')}>{money(view.totalDifference)}</strong>
      </p>
      <Card flush className="divide-y divide-slate-100">
        {view.sessions.length === 0 && <p className="p-6 text-center text-sm text-slate-500">Todavía no se abrió ninguna caja.</p>}
        {view.sessions.map((s) => {
          const diff = s.difference;
          return (
            <div key={s.id} className="flex items-center gap-3 p-3 text-sm sm:px-4">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">
                  {view.register(s.register_id)}
                  {s.device_only && ` (solo ${view.device(s.device_id)})`} · {dateFormat.format(new Date(s.opened_at))}
                  {s.closed_at && ` → ${dateFormat.format(new Date(s.closed_at))}`}
                </p>
                <p className="truncate text-slate-500">
                  Abrió {view.person(s.opened_by)} en {view.device(s.device_id)} con {money(s.opening_amount)}
                  {s.closed_at && ` · cerró ${view.person(s.closed_by)}`}
                  {s.close_notes && ` · “${s.close_notes}”`}
                </p>
              </div>
              <div className="shrink-0 text-right">
                {!s.closed_at ? (
                  <Badge tone="green">Abierta</Badge>
                ) : (
                  <>
                    <p className="tabular-nums">
                      {money(s.counted_cash ?? 0)}
                      {s.expected_cash != null && <span className="text-slate-500"> / {money(s.expected_cash)}</span>}
                    </p>
                    {diff != null && (
                      <p className={cx('font-medium tabular-nums', diff === 0 ? 'text-emerald-700' : diff < 0 ? 'text-red-700' : 'text-amber-700')}>
                        {diff === 0 ? 'Sin diferencia' : `${diff > 0 ? '+' : ''}${money(diff)}`}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </Card>
      <p className="text-xs text-slate-500">Contado / esperado. Rojo: faltante; amarillo: sobrante.</p>
    </div>
  );
}
